/**
 * Public-endpoint rate-limit middleware tags (client-safe).
 *
 * The public onboarding RPCs (`onboarding.*` in
 * `public/installations.rpc.ts`) are an unauthenticated-ish surface gated
 * only by the magic-link session token. Defense-in-depth per-IP (and
 * per-session) rate limiting prevents brute-forcing tokens/ids and DoS of
 * that surface (CLA-461 / M2).
 *
 * Only the tag + error declarations live here so client packages can import
 * the RPC group without pulling in any server-only dependency — the limiter
 * implementation lives in `rate-limit.middleware.ts`.
 *
 * @since 1.0.0
 */
import { RpcMiddleware } from "@effect/rpc";
import { Schema } from "effect";

/**
 * Typed Too Many Requests error returned when a caller exceeds the
 * per-IP / per-session rate limit on a public onboarding RPC.
 * `retryAfterMs` is how long until the current window expires.
 *
 * @since 1.0.0
 */
export class RateLimitedError extends Schema.TaggedError<RateLimitedError>()(
  "RateLimitedError",
  {
    message: Schema.String,
    retryAfterMs: Schema.Number,
  },
) {}

/**
 * RPC middleware enforcing the public-endpoint rate limit. Declared AFTER
 * `OnboardingSessionMiddleware` on each RPC so it wraps outermost and runs
 * FIRST — a breached request is rejected before the session middleware's
 * token lookup ever touches the database.
 *
 * @since 1.0.0
 */
export class PublicRateLimitMiddleware extends RpcMiddleware.Tag<PublicRateLimitMiddleware>()(
  "PublicRateLimitMiddleware",
  {
    failure: RateLimitedError,
  },
) {}
